/**
 * Single row in the staged-files list inside the contract upload
 * section.
 *
 * Renders an icon (PDF / image), the file name, the formatted size,
 * and a remove button. Pure presentation — the parent owns the staged
 * files state and removal logic, and the Container picks the icon and
 * binds the row's id into the remove handler (SE-8019).
 * @module features/contracts/components/ContractFileListItem/ContractFileListItemView
 */

import { Trash2 } from "lucide-react-native";
import React, { memo } from "react";

import { formatFileSize } from "@/features/contracts/utils/formatFileSize";

import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

const ROW_TEST_ID_PREFIX = "contract-file-list-item";
const REMOVE_BUTTON_TEST_ID_PREFIX = "contract-file-list-item-remove";

/** Props for {@link ContractFileListItemView}. */
interface Props {
  readonly id: string;
  readonly fileName: string;
  readonly fileSize: number;
  /** Row icon, picked from the MIME type by the Container. */
  readonly icon: React.ElementType;
  readonly disabled: boolean;
  /** Remove handler, already bound to this row's id by the Container. */
  readonly onRemovePress: () => void;
}

/**
 * Renders a single row in the staged-files list.
 * @param props - Row props
 * @param props.id - Stable identifier for React keys + the remove handler
 * @param props.fileName - File name to display
 * @param props.fileSize - Byte size to display (formatted via formatFileSize)
 * @param props.icon - Row icon, picked from the MIME type by the Container
 * @param props.disabled - When true, the remove button is disabled
 * @param props.onRemovePress - Invoked when the user removes the row
 * @returns The rendered file list row
 */
const ContractFileListItemView = ({
  id,
  fileName,
  fileSize,
  icon,
  disabled,
  onRemovePress,
}: Props) => (
  <Box testID={`${ROW_TEST_ID_PREFIX}-${id}`} className="w-full">
    <Stack
      direction="horizontal"
      className="items-center justify-between border-b border-surface-muted/60 p-3"
    >
      <Stack direction="horizontal" className="flex-1 items-center gap-3">
        <Icon as={icon} size="md" className="text-content-muted" />
        <Stack className="flex-1">
          <Text variant="body-strong" className="text-content-primary">
            {fileName}
          </Text>
          <Text variant="caption" className="text-content-muted">
            {formatFileSize(fileSize)}
          </Text>
        </Stack>
      </Stack>
      <Pressable
        testID={`${REMOVE_BUTTON_TEST_ID_PREFIX}-${id}`}
        onPress={onRemovePress}
        disabled={disabled}
        className="rounded-full p-2 hover:bg-surface-subtle active:bg-surface-muted"
      >
        <Icon as={Trash2} size="sm" className="text-status-error" />
      </Pressable>
    </Stack>
  </Box>
);

ContractFileListItemView.displayName = "ContractFileListItemView";

export default memo(ContractFileListItemView);
